import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import type { BreakingItem } from '@nk/shared';
import { useLocale } from '@/i18n/locale-context';
import { useClock, useReducedMotion } from '@/lib/hooks';
import type { StreamStatus } from '@/lib/use-event-stream';
import { Announce, cn } from '@/components/ui';

/**
 * The breaking-news ticker — PRD §5, and the strip at the very top of the supplied design.
 *
 * Three requirements from the Phase 7 DoD shape the whole component:
 *
 * - **It pauses on hover *and* on keyboard focus.** A marquee a reader cannot stop is a
 *   accessibility failure (WCAG 2.2.2); pausing only on hover fixes it for exactly the
 *   people who were never struggling.
 * - **`prefers-reduced-motion` gets a static rotator**, not a frozen marquee: the
 *   headlines still cycle, but by swapping text every few seconds instead of sliding.
 * - **The clock is client-side** (§1.12). Rendering it on the server would bake the time
 *   of the *cache fill* into a page served for the next sixty seconds.
 *
 * Phase 11 gave it live data (`useBreaking`): items arrive over SSE, a newly arrived one
 * is marked for a couple of seconds so a reader watching the strip sees it appear, and
 * the connection state is visible rather than hidden — a ticker that has stopped
 * listening should say so instead of looking current.
 */
export function Ticker({
  items = [],
  connection = 'idle',
  isPending = false,
}: {
  items?: BreakingItem[];
  connection?: StreamStatus;
  isPending?: boolean;
}) {
  const { t, intlTag } = useLocale();
  const reducedMotion = useReducedMotion();
  const clock = useClock(intlTag);
  const [paused, setPaused] = useState(false);
  const [index, setIndex] = useState(0);
  const fresh = useFreshItems(items);

  // The static alternative for reduced motion: one headline at a time, swapped on a timer
  // that respects the same pause as the marquee.
  useEffect(() => {
    if (!reducedMotion || paused || items.length < 2) return undefined;
    const timer = setInterval(() => setIndex((current) => (current + 1) % items.length), 6_000);
    return () => clearInterval(timer);
  }, [reducedMotion, paused, items.length]);

  const pauseHandlers = {
    onMouseEnter: () => setPaused(true),
    onMouseLeave: () => setPaused(false),
    onFocus: () => setPaused(true),
    onBlur: () => setPaused(false),
  };

  /**
   * What a screen reader is told (Phase 17). Only *arrivals* — never the rotation.
   * A marquee announced as it scrolls is a headline read aloud every few seconds over
   * whatever the reader was doing; a breaking story arriving is news.
   */
  const announcement = items.find((item) => fresh.has(item.id));

  return (
    <div
      // A landmark, so a screen-reader user can jump to the breaking strip or past it,
      // rather than meeting an unlabelled band of scrolling text.
      role="region"
      aria-label={t('ticker.label')}
      className="flex items-center gap-3.5 overflow-hidden border-b border-line bg-white px-gutter py-2.5 text-base text-ink"
      data-print-hide
      data-connection={connection}
      {...pauseHandlers}
    >
      <span className="shrink-0 rounded-xs bg-red px-2.5 py-1 text-xs font-extrabold tracking-wider text-white">
        {t('ticker.label')}
      </span>

      <div
        className="relative min-w-0 flex-1 overflow-hidden"
        style={{
          // Fades the headlines into the edges instead of clipping them mid-word.
          maskImage: 'linear-gradient(90deg, transparent, #000 4%, #000 92%, transparent)',
          WebkitMaskImage: 'linear-gradient(90deg, transparent, #000 4%, #000 92%, transparent)',
        }}
      >
        {items.length === 0 ? (
          // A skeleton means "loading". Once the answer is in and the answer is "nothing
          // is breaking", saying so is the honest version — a permanent shimmer on a
          // quiet news day would be a page pretending to still be working.
          isPending ? (
            <div className="nk-skeleton h-3 w-2/3 rounded-sm" aria-hidden="true" />
          ) : (
            <p className="truncate text-sm text-muted">{t('ticker.quiet')}</p>
          )
        ) : reducedMotion ? (
          <StaticRotator items={items} index={index} fresh={fresh} />
        ) : (
          <Marquee items={items} paused={paused} fresh={fresh} />
        )}
      </div>

      {/* `assertive`: this is the one thing on the site that earns an interruption. */}
      <Announce
        message={announcement ? `${t('ticker.label')}: ${announcement.headline}` : null}
        assertive
      />

      <ConnectionDot status={connection} />

      {/* Empty until the first client tick, which is what keeps hydration honest. */}
      <span className="hidden shrink-0 text-base font-bold tabular-nums text-ink md:block">
        {clock}
      </span>
    </div>
  );
}

/**
 * The ids that appeared since the last change, so the marquee can mark them.
 *
 * The very first list a reader sees is not "new" — everything on a freshly loaded page
 * would flash at once, which says nothing. Only what arrives afterwards is marked, and
 * only for as long as the animation runs.
 */
function useFreshItems(items: BreakingItem[]): Set<number> {
  const known = useRef<Set<number> | null>(null);
  const [fresh, setFresh] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    const ids = new Set(items.map((item) => item.id));
    if (known.current === null) {
      known.current = ids;
      return undefined;
    }

    const added = [...ids].filter((id) => !known.current?.has(id));
    known.current = ids;
    if (added.length === 0) return undefined;

    setFresh(new Set(added));
    // Slightly longer than the animation, so the class is gone before it could replay.
    const timer = setTimeout(() => setFresh(new Set()), 3_000);
    return () => clearTimeout(timer);
  }, [items]);

  return fresh;
}

/**
 * A quiet indicator: nothing at all while the stream is healthy, a muted dot while it is
 * reconnecting, and a labelled one once it has fallen back to polling. Silent success is
 * the right default — a permanent "connected" badge is noise on every page view.
 */
function ConnectionDot({ status }: { status: StreamStatus }) {
  const { t } = useLocale();
  if (status !== 'reconnecting' && status !== 'polling') return null;

  const label = status === 'polling' ? t('realtime.polling') : t('realtime.reconnecting');
  return (
    <span
      className="hidden shrink-0 items-center gap-1.5 text-xs font-semibold text-muted sm:flex"
      data-realtime-status={status}
      title={label}
    >
      <span
        className={cn(
          'inline-block h-1.5 w-1.5 rounded-full bg-muted',
          status === 'reconnecting' && 'animate-blink',
        )}
        aria-hidden="true"
      />
      <span className="sr-only">{label}</span>
    </span>
  );
}

function TickerLink({ item, fresh }: { item: BreakingItem; fresh: boolean }) {
  const className = cn('whitespace-nowrap rounded-xs px-1', fresh && 'animate-flash');
  if (!item.href) return <span className={className}>{item.headline}</span>;
  return (
    <Link to={item.href} className={cn(className, 'hover:text-red')}>
      {item.headline}
    </Link>
  );
}

/**
 * The sliding form. The list is rendered twice and translated by -50%, which is what makes
 * the loop seamless — a single copy visibly snaps back at the end.
 */
function Marquee({
  items,
  paused,
  fresh,
}: {
  items: BreakingItem[];
  paused: boolean;
  fresh: Set<number>;
}) {
  return (
    <div
      className={cn('inline-flex animate-ticker items-center gap-8 pl-[100%]')}
      style={{ animationPlayState: paused ? 'paused' : 'running' }}
    >
      {[0, 1].map((copy) => (
        <span key={copy} className="inline-flex items-center gap-8" aria-hidden={copy === 1}>
          {items.map((item) => (
            <span key={`${copy}-${item.id}`} className="inline-flex items-center gap-8">
              <TickerLink item={item} fresh={fresh.has(item.id)} />
              <span className="inline-block h-1 w-1 rounded-full bg-red" aria-hidden="true" />
            </span>
          ))}
        </span>
      ))}
    </div>
  );
}

function StaticRotator({
  items,
  index,
  fresh,
}: {
  items: BreakingItem[];
  index: number;
  fresh: Set<number>;
}) {
  // A newly arrived item interrupts the rotation: it is the one thing worth reading now.
  const freshIndex = items.findIndex((item) => fresh.has(item.id));
  const item = items[freshIndex >= 0 ? freshIndex : index % items.length];
  if (!item) return null;
  return (
    <p className="truncate" aria-live="off">
      <TickerLink item={item} fresh={fresh.has(item.id)} />
    </p>
  );
}
